import React, { useState, useEffect } from 'react';
import { callApi } from '../api';
import { Calendar, Trash2, CheckCircle, XCircle, AlertCircle, Clock, ChevronDown, ChevronRight, Filter, MessageCircle, Send, UserCheck, UserRound } from 'lucide-react';
import './YonetimSayfasi.css';

const BasvuruYonetim = ({ kulup }) => {
    const [basvurular, setBasvurular] = useState([]);
    const [etkinlikler, setEtkinlikler] = useState([]);
    const [waitlist, setWaitlist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);

    // NEW: Add Manual Registration State
    const [showAddModal, setShowAddModal] = useState(false);
    const [addForm, setAddForm] = useState({
        etkinlikID: '',
        adSoyad: '',
        ogrenciNo: '',
        telefon: '',
        tip: 'ASIL'
    });
    const [addLoading, setAddLoading] = useState(false);

    useEffect(() => {
        fetchAllData();
    }, [kulup]);

    const fetchAllData = async () => {
        try {
            setLoading(true);
            const query = kulup?.kulupID ? `?kulupID=${kulup.kulupID}` : '';

            const [basvuruData, etkinlikData, waitlistData] = await Promise.all([
                callApi(`/basvurular${query}`, 'GET'),
                callApi(`/etkinlikler${query}`, 'GET'),
                callApi(`/waitlist${query}`, 'GET').catch(() => ({ items: [] }))
            ]);

            setBasvurular(basvuruData.items || []);
            setEtkinlikler(etkinlikData.items || []);
            setWaitlist(waitlistData.items || []);
            setError(null);
        } catch (err) {
            console.error("Veriler yüklenirken hata:", err);
            setError("Veriler yüklenirken bir sorun oluştu.");
        } finally {
            setLoading(false);
        }
    };

    const handleDurumGuncelle = async (basvuruID, yeniDurum) => {
        if (!basvuruID) return;
        if (!window.confirm(`Bu başvuruyu ${yeniDurum} olarak işaretlemek istiyor musunuz?`)) return;

        try {
            setActionLoading(basvuruID);
            await callApi('/basvurular', 'PUT', { basvuruID, durum: yeniDurum });
            await fetchAllData();
            alert(`Başvuru başarıyla ${yeniDurum} yapıldı.`);
        } catch (err) {
            alert("İşlem başarısız oldu: " + err.message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleKatildiToggle = async (basvuruID, currentStatus) => {
        try {
            setActionLoading(basvuruID);
            const newStatus = currentStatus ? 0 : 1;
            await callApi('/basvurular', 'PUT', { basvuruID, katildi: newStatus });
            setBasvurular(prev => prev.map(p => p.basvuruID === basvuruID ? { ...p, katildi: newStatus } : p));
        } catch (err) {
            alert("Güncelleme hatası: " + err.message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleSendFeedbackMail = async (eventName) => {
        const eventObj = etkinlikler.find(e => e.baslik === eventName);
        if (!eventObj) return;

        const attendees = basvurular.filter(b => b.etkinlikID === eventObj.etkinlikID && b.katildi === 1 && !b.silindi);
        if (attendees.length === 0) {
            alert("Bu etkinlik için henüz 'Katıldı' olarak işaretlenmiş kimse yok. Önce katılımcıları işaretlemelisiniz.");
            return;
        }

        const formUrl = window.prompt(`${eventName} etkinliği için Google Form linkini giriniz:`, "");
        if (!formUrl || !formUrl.startsWith("http")) {
            if (formUrl !== null) alert("Geçerli bir link girmelisiniz.");
            return;
        }

        if (!window.confirm(`${attendees.length} kişiye geri bildirim maili gönderilecek. Onaylıyor musunuz?`)) return;

        try {
            setActionLoading(eventName);
            const res = await callApi(`/etkinlikler/${eventObj.etkinlikID}/send-feedback`, 'POST', { formUrl });
            alert(res.message || "Mailler başarıyla gönderildi!");
        } catch (err) {
            alert("Hata: " + err.message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleWaitlistDelete = async (entry) => {
        if (!window.confirm("Bu kaydı bekleme listesinden silmek istiyor musunuz?")) return;

        try {
            setActionLoading(entry.waitlistId);
            await callApi(`/waitlist?waitlistId=${entry.waitlistId}`, 'DELETE');
            await fetchAllData();
            alert("Bekleme listesinden silindi.");
        } catch (err) {
            alert("Silme işlemi başarısız: " + err.message);
        } finally {
            setActionLoading(null);
        }
    };

    // NEW MANUALLY ADD RECORD
    const handleAddSubmit = async (e) => {
        e.preventDefault();
        setAddLoading(true);
        try {
            const selectedEvent = etkinlikler.find(evt => evt.etkinlikID == addForm.etkinlikID || evt.id == addForm.etkinlikID);
            const payload = {
                uyeID: addForm.ogrenciNo || 'ADMIN_EKLEDI', // Fallback ID
                etkinlikID: addForm.etkinlikID,
                kulupID: kulup?.kulupID || 'klugastro',
                etkinlikBaslik: selectedEvent ? selectedEvent.baslik : 'Bilinmeyen Etkinlik',
                adSoyad: addForm.adSoyad,
                ogrenciNo: addForm.ogrenciNo,
                telefon: addForm.telefon
            };

            if (addForm.tip === 'WAITLIST') {
                await callApi('/waitlist', 'POST', { ...payload, tip: 'WAITLIST' });
                alert("Bekleme listesine başarıyla eklendi.");
            } else {
                await callApi('/basvurular', 'POST', payload);
                alert("Asil başvuru başarıyla eklendi.");
            }

            setShowAddModal(false);
            setAddForm({ etkinlikID: '', adSoyad: '', ogrenciNo: '', telefon: '', tip: 'ASIL' });
            await fetchAllData();
        } catch (error) {
            alert("Ekleme sırasında hata: " + error.message);
        } finally {
            setAddLoading(false);
        }
    };

    const [searchTerm, setSearchTerm] = useState("");
    const [expandedEvents, setExpandedEvents] = useState({});

    const toggleEvent = (eventName) => {
        setExpandedEvents(prev => ({ ...prev, [eventName]: !prev[eventName] }));
    };

    // Helper to group items by event
    const groupByEvent = (items) => {
        return items.reduce((groups, item) => {
            let title = null;

            // Prioritize lookup by ID to resolve to the current canonical title
            if (item.etkinlikID) {
                const matchedEvent = etkinlikler.find(e =>
                    String(e.etkinlikID) === String(item.etkinlikID) ||
                    String(e.id) === String(item.etkinlikID)
                );
                if (matchedEvent) title = matchedEvent.baslik;
            }

            // Fallback to snapshot title if lookup failed
            if (!title) {
                title = item.etkinlikBaslik || 'Diğer (Bilinmeyen)';
            }

            if (!groups[title]) groups[title] = [];
            groups[title].push(item);
            return groups;
        }, {});
    };

    const basvurularByEvent = groupByEvent(basvurular);
    const waitlistByEvent = groupByEvent(waitlist);

    const [showArchive, setShowArchive] = useState(false);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const allEventNames = new Set([
        ...Object.keys(basvurularByEvent),
        ...Object.keys(waitlistByEvent)
    ]);

    const activeEventNames = Array.from(allEventNames).filter(eventName => {
        if (eventName === 'Diğer (Bilinmeyen)') return false;
        if (showArchive) return true;

        const apps = basvurularByEvent[eventName] || [];
        const wl = waitlistByEvent[eventName] || [];

        const hasPendingApp = apps.some(a => a.durum === 'BEKLIYOR');
        const hasWaitlist = wl.length > 0;

        if (hasPendingApp || hasWaitlist) return true;

        const eventObj = etkinlikler.find(e => e.baslik === eventName);
        if (!eventObj) return false;

        const eventDate = new Date(eventObj.etkinlikTarihi || eventObj.tarih);
        eventDate.setHours(0, 0, 0, 0);

        return eventDate >= today;
    }).sort();

    const filteredEventNames = activeEventNames.filter(eventName => {
        const nameMatch = eventName.toLowerCase().includes(searchTerm.toLowerCase());

        const apps = basvurularByEvent[eventName] || [];
        const wl = waitlistByEvent[eventName] || [];

        const appMatch = apps.some(b =>
            (b.adSoyad && b.adSoyad.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (b.ogrenciNo && b.ogrenciNo.includes(searchTerm))
        );

        const wlMatch = wl.some(w =>
            (w.uyeID && w.uyeID.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        return nameMatch || appMatch || wlMatch;
    });

    if (loading) return <div className="loading-state"><div className="spinner"></div> Yükleniyor...</div>;

    return (
        <div className="yonetim-container">
            <h2 className="page-title">Ödeme Bildirimleri & Başvurular</h2>
            <p className="page-desc">Bekleyen başvurular ve bekleme listeleri burada yönetilir.</p>

            {error && <div className="error-message">{error}</div>}

            {/* --- Search & Filters & Add --- */}
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap' }}>
                <div className="search-bar-wrapper" style={{ flex: 1, position: 'relative', minWidth: '280px' }}>
                    <Filter className="search-icon" size={20} color="#94a3b8" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
                    <input
                        type="text"
                        placeholder="İsim, numara veya etkinlik ara..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '14px 14px 14px 48px',
                            fontSize: '0.95rem',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                            outline: 'none',
                            transition: 'border-color 0.2s'
                        }}
                    />
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                        onClick={() => setShowAddModal(true)}
                        style={{
                            padding: '12px 20px',
                            background: '#2563eb',
                            border: '1px solid #1d4ed8',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: 'white',
                            fontWeight: '600',
                            boxShadow: '0 4px 10px rgba(37,99,235,0.2)'
                        }}
                    >
                        <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>+</span> Yeni Kayıt
                    </button>

                    <button
                        onClick={() => setShowArchive(!showArchive)}
                        className={`archive-toggle-btn ${showArchive ? 'active' : ''}`}
                    style={{
                        padding: '12px 20px',
                        background: showArchive ? '#f1f5f9' : 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: showArchive ? '#0f172a' : '#64748b',
                        fontWeight: '500',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                    }}
                >
                    <Clock size={18} />
                    {showArchive ? 'Arşivi Gizle' : 'Geçmişi Göster'}
                </button>
                </div>
            </div>

            <div className="groups-container">
                {filteredEventNames.length === 0 ? (
                    <div className="empty-state" style={{ textAlign: 'center', padding: '60px', background: 'white', borderRadius: '16px', border: '1px dashed #cbd5e0' }}>
                        <div className="empty-icon" style={{ marginBottom: '16px' }}><Calendar size={48} color="#cbd5e0" /></div>
                        <h3 style={{ color: '#64748b', margin: 0 }}>Kayıt Bulunamadı</h3>
                        <p style={{ color: '#94a3b8', marginTop: '8px' }}>{showArchive ? 'Arama kriterlerine uygun sonuç yok.' : 'Bekleyen işlem yok.'}</p>
                    </div>
                ) : (
                    filteredEventNames.map(eventName => {
                        const items = basvurularByEvent[eventName] || [];
                        const wlItems = waitlistByEvent[eventName] || [];

                        // Sort Logic
                        const sortedItems = [...items].sort((a, b) => {
                            if (a.silindi && !b.silindi) return 1; /* Silinenleri en alta at */
                            if (!a.silindi && b.silindi) return -1;
                            if (a.durum === 'BEKLIYOR' && b.durum !== 'BEKLIYOR') return -1;
                            if (a.durum !== 'BEKLIYOR' && b.durum === 'BEKLIYOR') return 1;
                            return new Date(b.tarih) - new Date(a.tarih);
                        });
                        wlItems.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

                        const eventObj = etkinlikler.find(e => e.baslik === eventName);
                        const eventDateStr = eventObj?.etkinlikTarihi || eventObj?.tarih;
                        const isPast = eventDateStr && new Date(eventDateStr) < today;
                        const pendingCount = sortedItems.filter(i => i.durum === 'BEKLIYOR' && !i.silindi).length;
                        const approvedCount = sortedItems.filter(i => i.durum === 'ONAYLANDI' && !i.silindi).length;
                        const rejectedCount = sortedItems.filter(i => i.durum === 'REDDEDILDI' && !i.silindi).length;
                        const isExpanded = expandedEvents[eventName];

                        return (
                            <div key={eventName} className={`event-group-card ${isPast ? 'archived-event' : ''}`} style={{ marginBottom: '24px', background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                {/* Header */}
                                <div
                                    className={`group-header ${isExpanded ? 'active' : ''}`}
                                    onClick={() => toggleEvent(eventName)}
                                    style={{
                                        padding: '20px 24px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        background: isExpanded ? '#f8fafc' : 'white',
                                        borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none',
                                        transition: 'background 0.2s'
                                    }}
                                >
                                    <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        <div className={`header-icon ${isPast ? 'past' : ''}`} style={{
                                            width: '40px', height: '40px', borderRadius: '10px',
                                            background: isPast ? '#f1f5f9' : '#eff6ff',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                                        }}>
                                            <Calendar size={20} color={isPast ? '#64748b' : '#3b82f6'} />
                                        </div>
                                        <div>
                                            <h3 className="event-title" style={{ margin: 0, fontSize: '1.05rem', color: '#1e293b', fontWeight: '600' }}>
                                                {eventName}
                                                {isPast && <span className="badge-past" style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#64748b', background: '#f1f5f9', padding: '2px 8px', borderRadius: '4px' }}>Arşiv</span>}
                                            </h3>
                                            <div className="event-meta" style={{ display: 'flex', gap: '8px', marginTop: '8px', fontSize: '0.75rem', color: '#64748b', flexWrap: 'wrap' }}>
                                                <span style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center' }}>
                                                    Toplam: <strong style={{ marginLeft: '4px' }}>{sortedItems.length}</strong>
                                                </span>
                                                
                                                {approvedCount > 0 && (
                                                    <span style={{ color: '#166534', background: '#dcfce7', padding: '2px 8px', borderRadius: '4px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <CheckCircle size={12} /> {approvedCount} Onaylanan
                                                    </span>
                                                )}

                                                {pendingCount > 0 && (
                                                    <span style={{ color: '#854d0e', background: '#fef9c3', padding: '2px 8px', borderRadius: '4px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <AlertCircle size={12} /> {pendingCount} Bekleyen
                                                    </span>
                                                )}

                                                {rejectedCount > 0 && (
                                                    <span style={{ color: '#991b1b', background: '#fee2e2', padding: '2px 8px', borderRadius: '4px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <XCircle size={12} /> {rejectedCount} Reddedilen
                                                    </span>
                                                )}

                                                {wlItems.length > 0 && (
                                                    <span style={{ color: '#b45309', background: '#fffbeb', padding: '2px 8px', borderRadius: '4px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <Clock size={12} /> {wlItems.length} Sırada (Waitlist)
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        {!isPast && pendingCount === 0 && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleSendFeedbackMail(eventName); }}
                                                className="feedback-btn"
                                                style={{
                                                    padding: '8px 16px',
                                                    background: '#f8fafc',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '8px',
                                                    color: '#e67e22',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '600',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                <Send size={16} /> Geri Bildirim Gönder
                                            </button>
                                        )}
                                        <div className="header-arrow" style={{ color: '#94a3b8' }}>
                                            {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                        </div>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="group-content animate-slide-down" style={{ padding: '0' }}>

                                        {/* --- APPLICATIONS TABLE --- */}
                                        {sortedItems.length > 0 && (
                                            <div style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                                <table className="basvuru-tablosu" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                            <th style={{ padding: '16px 24px', textAlign: 'left', width: '30%', color: '#64748b', fontWeight: '600', fontSize: '0.85rem' }}>ÖĞRENCİ</th>
                                                            <th style={{ padding: '16px', textAlign: 'left', width: '20%', color: '#64748b', fontWeight: '600', fontSize: '0.85rem' }}>İLETİŞİM</th>
                                                            <th style={{ padding: '16px', textAlign: 'center', width: '15%', color: '#64748b', fontWeight: '600', fontSize: '0.85rem' }}>TARİH</th>
                                                            <th style={{ padding: '16px', textAlign: 'center', width: '12%', color: '#64748b', fontWeight: '600', fontSize: '0.85rem' }}>DURUM</th>
                                                            <th style={{ padding: '16px', textAlign: 'center', width: '10%', color: '#64748b', fontWeight: '600', fontSize: '0.85rem' }}>KATILIM</th>
                                                            <th style={{ padding: '16px 24px', textAlign: 'right', width: '13%', color: '#64748b', fontWeight: '600', fontSize: '0.85rem' }}>İŞLEM</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sortedItems.map(basvuru => (
                                                            <tr key={basvuru.basvuruID} style={{ borderBottom: '1px solid #f1f5f9', background: basvuru.silindi ? '#fef2f2' : 'white' }}>
                                                                <td data-label="Öğrenci" style={{ padding: '16px 24px', verticalAlign: 'middle' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                        <div style={{
                                                                            width: '32px', height: '32px', borderRadius: '50%',
                                                                            background: basvuru.silindi ? '#fee2e2' : '#eff6ff',
                                                                            color: basvuru.silindi ? '#ef4444' : '#3b82f6',
                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                            fontWeight: 'bold', fontSize: '0.9rem', flexShrink: 0
                                                                        }}>
                                                                            {basvuru.adSoyad?.charAt(0) || '?'}
                                                                        </div>
                                                                        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                <span style={{ fontWeight: '600', color: basvuru.silindi ? '#ef4444' : '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                                    {basvuru.adSoyad}
                                                                                </span>
                                                                                {basvuru.kayitTipi && (
                                                                                    <span style={{
                                                                                        fontSize: '0.65rem',
                                                                                        fontWeight: '700',
                                                                                        padding: '1px 5px',
                                                                                        borderRadius: '4px',
                                                                                        background: basvuru.kayitTipi === 'YEDEK' ? '#fef3c7' : '#e0f2fe',
                                                                                        color: basvuru.kayitTipi === 'YEDEK' ? '#92400e' : '#0369a1'
                                                                                    }}>
                                                                                        {basvuru.kayitTipi === 'YEDEK' ? `Yedek #${basvuru.siraNo || ''}` : `Katılımcı #${basvuru.siraNo || ''}`}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{basvuru.ogrenciNo}</span>
                                                                            {basvuru.silindi && <span style={{ fontSize: '0.7rem', color: '#ef4444', fontWeight: 'bold' }}>SİLİNDİ</span>}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td data-label="İletişim" style={{ padding: '16px', verticalAlign: 'middle', color: '#475569', fontSize: '0.9rem' }}>
                                                                    {basvuru.telefon}
                                                                </td>
                                                                <td data-label="Tarih" style={{ padding: '16px', verticalAlign: 'middle', textAlign: 'center' }}>
                                                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#64748b', fontSize: '0.75rem', background: '#f8fafc', padding: '2px 6px', borderRadius: '4px' }}>
                                                                        <Clock size={12} /> {new Date(basvuru.tarih).toLocaleDateString()} {new Date(basvuru.tarih).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                    </div>
                                                                </td>
                                                                <td data-label="Durum" style={{ padding: '16px', verticalAlign: 'middle', textAlign: 'center' }}>
                                                                    <span className={`status-pill status-${basvuru.durum}`} style={{
                                                                        display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '700',
                                                                        background: basvuru.durum === 'ONAYLANDI' ? '#dcfce7' : basvuru.durum === 'REDDEDILDI' ? '#fee2e2' : '#fef9c3',
                                                                        color: basvuru.durum === 'ONAYLANDI' ? '#166534' : basvuru.durum === 'REDDEDILDI' ? '#991b1b' : '#854d0e'
                                                                    }}>
                                                                        {basvuru.durum}
                                                                    </span>
                                                                </td>
                                                                <td data-label="Katılım" style={{ padding: '16px', verticalAlign: 'middle', textAlign: 'center' }}>
                                                                    <button
                                                                        onClick={() => handleKatildiToggle(basvuru.basvuruID, basvuru.katildi)}
                                                                        disabled={actionLoading === basvuru.basvuruID || basvuru.durum !== 'ONAYLANDI' || basvuru.silindi}
                                                                        style={{
                                                                            background: basvuru.katildi ? '#10b981' : '#f1f5f9',
                                                                            border: 'none',
                                                                            borderRadius: '20px',
                                                                            padding: '4px 12px',
                                                                            color: basvuru.katildi ? 'white' : '#64748b',
                                                                            fontSize: '0.75rem',
                                                                            fontWeight: 'bold',
                                                                            cursor: (basvuru.durum === 'ONAYLANDI' && !basvuru.silindi) ? 'pointer' : 'not-allowed',
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '4px',
                                                                            transition: 'all 0.2s',
                                                                            opacity: (basvuru.durum !== 'ONAYLANDI' || basvuru.silindi) ? 0.5 : 1
                                                                        }}
                                                                    >
                                                                        {basvuru.katildi ? <UserCheck size={14} /> : <UserRound size={14} />}
                                                                        {basvuru.katildi ? 'Katıldı' : 'Bekliyor'}
                                                                    </button>
                                                                </td>
                                                                <td data-label="İşlem" style={{ padding: '16px 24px', verticalAlign: 'middle', textAlign: 'right' }}>
                                                                    {basvuru.silindi ? (
                                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                                                                            <button
                                                                                title="Geri Yükle"
                                                                                onClick={async () => {
                                                                                    if (!window.confirm('Başvuru geri yüklensin mi?')) return;
                                                                                    try {
                                                                                        setActionLoading(basvuru.basvuruID);
                                                                                        await callApi('/basvurular', 'PUT', { basvuruID: basvuru.basvuruID, silindi: false });
                                                                                        setBasvurular(prev => prev.map(p => p.basvuruID === basvuru.basvuruID ? { ...p, silindi: false } : p));
                                                                                    } catch (e) { alert(e.message); } finally { setActionLoading(null); }
                                                                                }}
                                                                                style={{ background: '#f0f9ff', border: '1px solid #bae6fd', color: '#0284c7', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: '500' }}
                                                                            >
                                                                                <Clock size={16} /> Geri Yükle
                                                                            </button>
                                                                            <button
                                                                                title="Kalıcı Olarak Sil"
                                                                                onClick={async () => {
                                                                                    if (!window.confirm('DİKKAT: Bu başvuru veritabanından kalıcı olarak silinecektir. Bu işlemin geri dönüşü YOKTUR. Onaylıyor musunuz?')) return;
                                                                                    try {
                                                                                        setActionLoading(basvuru.basvuruID);
                                                                                        await callApi(`/basvurular?basvuruID=${basvuru.basvuruID}&permanent=true`, 'DELETE');
                                                                                        setBasvurular(prev => prev.filter(p => p.basvuruID !== basvuru.basvuruID));
                                                                                    } catch (e) { alert("Veritabanı silme hatası: " + e.message); } finally { setActionLoading(null); }
                                                                                }}
                                                                                style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: '500' }}
                                                                            >
                                                                                <Trash2 size={16} /> Sil
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                                                                            {basvuru.durum !== 'ONAYLANDI' && (
                                                                                <button
                                                                                    title="Onayla"
                                                                                    onClick={() => handleDurumGuncelle(basvuru.basvuruID, 'ONAYLANDI')}
                                                                                    disabled={actionLoading === basvuru.basvuruID}
                                                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#22c55e', padding: '6px', borderRadius: '6px', transition: 'bg 0.2s' }}
                                                                                    onMouseOver={(e) => e.currentTarget.style.background = '#dcfce7'}
                                                                                    onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                                                                                >
                                                                                    <CheckCircle size={20} />
                                                                                </button>
                                                                            )}
                                                                            {basvuru.durum !== 'REDDEDILDI' && (
                                                                                <button
                                                                                    title="Reddet"
                                                                                    onClick={() => handleDurumGuncelle(basvuru.basvuruID, 'REDDEDILDI')}
                                                                                    disabled={actionLoading === basvuru.basvuruID}
                                                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '6px', borderRadius: '6px', transition: 'bg 0.2s' }}
                                                                                    onMouseOver={(e) => e.currentTarget.style.background = '#fee2e2'}
                                                                                    onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                                                                                >
                                                                                    <XCircle size={20} />
                                                                                </button>
                                                                            )}
                                                                            <button
                                                                                title="Sil"
                                                                                onClick={async () => {
                                                                                    if (!window.confirm('Bu başvuru silinsin mi?')) return;
                                                                                    try {
                                                                                        await callApi(`/basvurular?basvuruID=${basvuru.basvuruID}`, 'DELETE');
                                                                                        setBasvurular(prev => prev.map(p => p.basvuruID === basvuru.basvuruID ? { ...p, silindi: true } : p));
                                                                                    } catch (e) { alert(e.message); }
                                                                                }}
                                                                                disabled={actionLoading === basvuru.basvuruID}
                                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '6px', borderRadius: '6px', transition: 'bg 0.2s' }}
                                                                                onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                                                                onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                                                                            >
                                                                                <Trash2 size={18} />
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {/* --- WAITLIST TABLE --- */}
                                        {wlItems.length > 0 && (
                                            <div style={{ padding: '0', borderTop: '4px solid #fff7ed', background: '#fffbeb' }}>
                                                <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', gap: '8px', color: '#b45309', borderBottom: '1px solid #fef3c7' }}>
                                                    <Clock size={18} />
                                                    <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>BEKLEME LİSTESİ (WAITLIST)</h4>
                                                </div>

                                                <table className="basvuru-tablosu" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: '1px solid #fef3c7' }}>
                                                            <th style={{ padding: '12px 24px', width: '80px', textAlign: 'center', color: '#b45309', fontSize: '0.8rem' }}>SIRA</th>
                                                            <th style={{ padding: '12px 16px', textAlign: 'left', width: '30%', color: '#b45309', fontSize: '0.8rem' }}>ÜYE BİLGİSİ</th>
                                                            <th style={{ padding: '12px 16px', textAlign: 'center', width: '20%', color: '#b45309', fontSize: '0.8rem' }}>KAYIT TARİHİ</th>
                                                            <th style={{ padding: '12px 16px', textAlign: 'center', width: '15%', color: '#b45309', fontSize: '0.8rem' }}>DURUM</th>
                                                            <th style={{ padding: '12px 24px', textAlign: 'right', width: 'auto', color: '#b45309', fontSize: '0.8rem' }}>İŞLEMLER</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {wlItems.map((entry, idx) => (
                                                            <tr key={entry.waitlistId} style={{ borderBottom: '1px solid #fef3c7' }}>
                                                                <td data-label="Sıra" style={{ padding: '12px 24px', textAlign: 'center' }}>
                                                                    <div style={{
                                                                        width: '28px', height: '28px', borderRadius: '50%',
                                                                        background: idx === 0 ? '#f59e0b' : '#fffbeb',
                                                                        border: idx === 0 ? 'none' : '1px solid #fcd34d',
                                                                        color: idx === 0 ? 'white' : '#b45309',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        fontWeight: 'bold', fontSize: '0.9rem', margin: '0 auto'
                                                                    }}>
                                                                        {idx + 1}
                                                                    </div>
                                                                </td>
                                                                <td data-label="Üye Bilgisi" style={{ padding: '12px 16px' }}>
                                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                        <span style={{ fontWeight: '600', color: '#78350f', fontSize: '0.95rem' }}>{entry.adSoyad || entry.uyeID}</span>
                                                                        <span style={{ fontSize: '0.8rem', color: '#92400e', opacity: 0.8 }}>{entry.telefon}</span>
                                                                    </div>
                                                                </td>
                                                                <td data-label="Kayıt Tarihi" style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                                    <span style={{ fontSize: '0.85rem', color: '#92400e', fontFamily: 'monospace' }}>
                                                                        {new Date(entry.createdAt).toLocaleDateString()} {new Date(entry.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                    </span>
                                                                </td>
                                                                <td data-label="Durum" style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                                    {entry.durum === 'BILGILENDIRILDI' ? (
                                                                        <span style={{ fontSize: '0.75rem', padding: '4px 8px', borderRadius: '12px', background: '#dcfce7', color: '#166534', fontWeight: '600' }}>Bilgilendirildi</span>
                                                                    ) : (
                                                                        <span style={{ fontSize: '0.75rem', padding: '4px 8px', borderRadius: '12px', background: '#ffe4e6', color: '#be123c', fontWeight: '600' }}>Sırada</span>
                                                                    )}
                                                                </td>
                                                                <td data-label="İşlemler" style={{ padding: '12px 24px', textAlign: 'right' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                                                                        {/* APPROVE BUTTON - Primary Action */}
                                                                        <button
                                                                            onClick={async () => {
                                                                                if (!window.confirm(`${entry.adSoyad || 'Kullanıcı'} bekleme listesinden onaylanıp asil listeye alınsın mı?`)) return;
                                                                                try {
                                                                                    setActionLoading(entry.waitlistId);
                                                                                    await callApi('/waitlist/approve', 'POST', {
                                                                                        waitlistId: entry.waitlistId,
                                                                                        etkinlikID: entry.etkinlikID,
                                                                                        uyeID: entry.uyeID,
                                                                                        kulupID: entry.kulupID
                                                                                    });
                                                                                    await fetchAllData();
                                                                                    alert("Kullanıcı onaylandı ve listeye eklendi!");
                                                                                } catch (e) { alert("Hata: " + e.message); } finally { setActionLoading(null); }
                                                                            }}
                                                                            title="Onayla ve Listeye Al"
                                                                            disabled={actionLoading === entry.waitlistId}
                                                                            style={{
                                                                                background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px',
                                                                                padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                                                                                fontSize: '0.85rem', fontWeight: '600', boxShadow: '0 2px 4px rgba(34, 197, 94, 0.2)'
                                                                            }}
                                                                        >
                                                                            <CheckCircle size={16} /> Onayla
                                                                        </button>

                                                                        <a
                                                                            href={`https://wa.me/?text=Merhaba ${entry.adSoyad ? entry.adSoyad.split(' ')[0] : ''}, "${eventName}" etkinliği için kontenjan açıldı!`}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            title="WhatsApp"
                                                                            style={{
                                                                                background: '#dcfce7', color: '#166534', borderRadius: '6px', padding: '6px',
                                                                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                                            }}
                                                                        >
                                                                            <MessageCircle size={18} />
                                                                        </a>

                                                                        <button
                                                                            onClick={() => handleWaitlistDelete(entry)}
                                                                            title="Sil"
                                                                            disabled={actionLoading === entry.waitlistId}
                                                                            style={{
                                                                                background: 'white', border: '1px solid #fee2e2', color: '#ef4444', borderRadius: '6px',
                                                                                padding: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center'
                                                                            }}
                                                                        >
                                                                            <Trash2 size={16} />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {sortedItems.length === 0 && wlItems.length === 0 && (
                                            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Kayıt bulunamadı.</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* NEW ADD MODAL */}
            {showAddModal && (
                <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
                    <div className="modal-content" style={{ background: 'white', padding: '2rem', borderRadius: '16px', width: '100%', maxWidth: '500px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#0f172a' }}>Manuel Kayıt Ekle</h3>
                            <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}>×</button>
                        </div>
                        <form onSubmit={handleAddSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '600', color: '#475569' }}>Kayıt Tipi</label>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', background: addForm.tip === 'ASIL' ? '#eff6ff' : '#f8fafc', padding: '0.75rem', borderRadius: '8px', border: `1px solid ${addForm.tip === 'ASIL' ? '#3b82f6' : '#e2e8f0'}`, flex: 1 }}>
                                        <input type="radio" name="tip" value="ASIL" checked={addForm.tip === 'ASIL'} onChange={(e) => setAddForm({ ...addForm, tip: e.target.value })} />
                                        <span style={{ fontWeight: addForm.tip === 'ASIL' ? '600' : 'normal', color: addForm.tip === 'ASIL' ? '#1d4ed8' : '#475569' }}>Asil Başvuru</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', background: addForm.tip === 'WAITLIST' ? '#fffbeb' : '#f8fafc', padding: '0.75rem', borderRadius: '8px', border: `1px solid ${addForm.tip === 'WAITLIST' ? '#f59e0b' : '#e2e8f0'}`, flex: 1 }}>
                                        <input type="radio" name="tip" value="WAITLIST" checked={addForm.tip === 'WAITLIST'} onChange={(e) => setAddForm({ ...addForm, tip: e.target.value })} />
                                        <span style={{ fontWeight: addForm.tip === 'WAITLIST' ? '600' : 'normal', color: addForm.tip === 'WAITLIST' ? '#b45309' : '#475569' }}>Bekleme Listesi</span>
                                    </label>
                                </div>
                            </div>
                            
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '600', color: '#475569' }}>Etkinlik Seçimi</label>
                                <select required value={addForm.etkinlikID} onChange={(e) => setAddForm({ ...addForm, etkinlikID: e.target.value })} style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', outline: 'none' }}>
                                    <option value="">Seçiniz</option>
                                    {etkinlikler.filter(e => new Date(e.etkinlikTarihi || e.tarih) > new Date(new Date().setHours(0,0,0,0) - 86400000)).map(e => (
                                        <option key={e.etkinlikID || e.id} value={e.etkinlikID || e.id}>{e.baslik}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '600', color: '#475569' }}>İsim Soyisim</label>
                                <input required type="text" placeholder="Ad Soyad" value={addForm.adSoyad} onChange={(e) => setAddForm({ ...addForm, adSoyad: e.target.value })} style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', outline: 'none' }} />
                            </div>

                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '600', color: '#475569' }}>Öğrenci No</label>
                                    <input type="text" placeholder="Opsiyonel" value={addForm.ogrenciNo} onChange={(e) => setAddForm({ ...addForm, ogrenciNo: e.target.value })} style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', outline: 'none' }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '600', color: '#475569' }}>Telefon</label>
                                    <input type="text" placeholder="05XXXXXXXXX" value={addForm.telefon} onChange={(e) => setAddForm({ ...addForm, telefon: e.target.value })} style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', outline: 'none' }} />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                                <button type="button" onClick={() => setShowAddModal(false)} style={{ flex: 1, padding: '0.75rem', background: '#f1f5f9', border: 'none', borderRadius: '8px', fontWeight: 'bold', color: '#475569', cursor: 'pointer' }}>İptal</button>
                                <button type="submit" disabled={addLoading} style={{ flex: 1, padding: '0.75rem', background: '#2563eb', border: 'none', borderRadius: '8px', fontWeight: 'bold', color: 'white', cursor: 'pointer', opacity: addLoading ? 0.7 : 1 }}>{addLoading ? 'Kaydediliyor...' : 'Kaydet'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BasvuruYonetim;
